import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, MapPin, ChevronDown } from "lucide-react";
import BotonAcceso from "../elementos/BotonAcceso.jsx";
import { useTheme } from "../guardarToken/themeContext";

const apiUrlPlatos = process.env.REACT_APP_API_PLATOS || '/api/menus/platos/';
// Determina base API: si es absoluta, respétala; si es relativa usa el mismo origen del frontend (producción)
const apiBaseUrl = apiUrlPlatos.startsWith('http')
    ? apiUrlPlatos.replace(/\/platos\/?$/, '')
    : `${window.location.origin}${apiUrlPlatos.replace(/\/platos\/?$/, '')}`;

const CrearPlatos = () => {
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [tipo, setTipo] = useState('');
    const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
    const [sedes, setSedes] = useState([]);
    const [tiposMenu, setTiposMenu] = useState([]);
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    // Cargar tipos de menú y sedes desde la API
    useEffect(() => {
        const obtenerTipos = async () => {
            try {
                const response = await axios.get(`${apiBaseUrl}/tipos-menu`);
                setTiposMenu(response.data);
            } catch (error) {
                console.error('Error al cargar tipos de menú:', error);
            }
        };

        const obtenerSedes = async () => {
            try {
                const response = await axios.get(`${apiBaseUrl}/sedes`);
                setSedes(response.data.filter(s => s.activo));
            } catch (error) {
                console.error('Error al cargar sedes:', error);
            }
        };

        obtenerTipos();
        obtenerSedes();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        switch (name) {
            case 'nombre': setNombre(value); break;
            case 'descripcion': setDescripcion(value); break;
            case 'tipo': setTipo(value); break;
            default: break;
        }
    };

    const handleSedeToggle = (sedeId) => {
        setSedesSeleccionadas(prev =>
            prev.includes(sedeId)
                ? prev.filter(id => id !== sedeId)
                : [...prev, sedeId]
        );
    };

    const store = async (e) => {
        e.preventDefault();

        try {
            await axios.post(apiUrlPlatos, {
                nombre,
                descripcion,
                tipo,
                sede_ids: sedesSeleccionadas
            });
            navigate('/admin/AbmPlatos');
        } catch (error) {
            console.error('Error al crear el plato:', error);
        }
    };

    const Back = () => {
        navigate('/admin/AbmPlatos');
    };

    return (
        <div className={`min-h-screen p-3 lg:p-6 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-[#f8f5e9] text-gray-800"}`}>

            {/* HEADER COMPACT */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-2xl shadow-sm p-5 mb-8 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700 max-w-4xl mx-auto`}>
                <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-700 p-3 rounded-xl shadow-lg shadow-orange-900/20">
                        <Plus className="text-white w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black tracking-tight leading-none mb-1">Crear Nuevo Plato</h1>
                        <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-gray-500"} font-black uppercase tracking-wider`}>Ingresa los detalles para la oferta gastronómica</p>
                    </div>
                </div>
                <button
                    onClick={Back}
                    className={`mt-4 md:mt-0 px-5 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${isDarkMode ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 shadow-sm"
                        }`}
                >
                    Volver al Listado
                </button>
            </div>

            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} max-w-4xl mx-auto rounded-3xl shadow-2xl border overflow-hidden animate-in slide-in-from-bottom duration-1000`}>
                <div className="p-1 bg-gradient-to-r from-orange-500 to-orange-700"></div>

                <form onSubmit={store} className="p-8 md:p-10 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* LEFT COLUMN: BASIC INFO */}
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 ml-1">Nombre del Plato</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    placeholder="Ej: Pechuga con arroz azafranado"
                                    value={nombre}
                                    onChange={handleInputChange}
                                    className={`w-full px-5 py-3.5 rounded-2xl border text-sm font-bold focus:outline-none transition-all ${isDarkMode
                                        ? "bg-slate-950 border-slate-800 text-white focus:border-orange-500/50"
                                        : "bg-gray-50 border-gray-200 text-gray-800 focus:border-orange-500"
                                        }`}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 ml-1">Descripción</label>
                                <textarea
                                    name="descripcion"
                                    placeholder="Ingresa los ingredientes o detalles clave..."
                                    value={descripcion}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className={`w-full px-5 py-3.5 rounded-2xl border text-sm font-medium focus:outline-none transition-all resize-none ${isDarkMode
                                        ? "bg-slate-950 border-slate-800 text-white focus:border-orange-500/50"
                                        : "bg-gray-50 border-gray-200 text-gray-800 focus:border-orange-500"
                                        }`}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 ml-1">Categoría / Tipo</label>
                                <div className="relative">
                                    <select
                                        name="tipo"
                                        value={tipo}
                                        onChange={handleInputChange}
                                        required
                                        className={`w-full px-5 py-3.5 rounded-2xl border text-sm font-bold appearance-none focus:outline-none transition-all ${isDarkMode
                                            ? "bg-slate-950 border-slate-800 text-white focus:border-orange-500/50"
                                            : "bg-gray-50 border-gray-200 text-gray-800 focus:border-orange-500"
                                            }`}
                                    >
                                        <option value="">Seleccione una categoría</option>
                                        {tiposMenu.map(t => (
                                            <option key={t.id} value={t.nombre} className={isDarkMode ? "bg-slate-900" : "bg-white"}>
                                                {t.nombre}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-5 flex items-center pointer-events-none">
                                        <ChevronDown className="w-4 h-4 text-orange-500" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: SEDES */}
                        <div className="flex flex-col h-full">
                            <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 ml-1 mb-4 flex items-center gap-2">
                                <MapPin className="w-3 h-3" /> Disponibilidad en Sedes
                            </label>
                            <div className={`flex-1 rounded-2xl p-4 space-y-2 ${isDarkMode ? "bg-slate-950/50" : "bg-gray-50 border border-gray-100 shadow-inner"}`}>
                                {sedes.length > 0 ? sedes.map(sede => (
                                    <label
                                        key={sede.id}
                                        className={`flex items-center gap-4 px-4 py-3 rounded-xl border cursor-pointer transition-all group ${sedesSeleccionadas.includes(sede.id)
                                            ? "bg-orange-500/10 border-orange-500/50 text-orange-500 shadow-sm"
                                            : (isDarkMode ? "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700" : "bg-white border-gray-200 text-gray-500 hover:border-orange-200")
                                            }`}
                                    >
                                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${sedesSeleccionadas.includes(sede.id)
                                            ? "bg-orange-500 border-orange-500"
                                            : (isDarkMode ? "border-slate-700" : "border-gray-200")
                                            }`}>
                                            {sedesSeleccionadas.includes(sede.id) && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={sedesSeleccionadas.includes(sede.id)}
                                            onChange={() => handleSedeToggle(sede.id)}
                                            className="hidden"
                                        />
                                        <span className={`text-xs font-black uppercase tracking-wider ${sedesSeleccionadas.includes(sede.id) ? "text-orange-500" : ""}`}>
                                            {sede.nombre}
                                        </span>
                                    </label>
                                )) : (
                                    <p className="text-[10px] text-slate-500 italic p-4 text-center">Cargando sedes...</p>
                                )}
                            </div>
                            <p className="mt-3 text-[9px] text-slate-500 font-bold italic text-center">Selecciona las sedes donde este plato figurará en el menú</p>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-slate-700/10 flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={Back}
                            className={`px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${isDarkMode ? "text-slate-500 hover:text-white hover:bg-slate-800" : "text-gray-400 hover:text-gray-800 hover:bg-gray-100"
                                }`}
                        >
                            Cancelar
                        </button>
                        <BotonAcceso
                            variant="accent"
                            type="submit"
                            className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-3.5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-orange-950/20 transform hover:scale-[1.02] transition-all"
                        >
                            Crear Plato
                        </BotonAcceso>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CrearPlatos;

